export interface StoredTokens {
  /** JWT ID token returned by Cognito */
  idToken: string;
  /** OAuth access token (may be JWT depending on configuration) */
  accessToken: string;
  /** Optional refresh token (not always issued to SPAs) */
  refreshToken?: string;
  /** Absolute expiry time as epoch seconds (UTC) */
  expiresAt: number;
  tokenType: 'Bearer';
}

export const TokenStoreKeys = {
  tokens: 'cognito_demo.tokens',
  pendingLogin: 'cognito_demo.pending_login',
} as const;

export type TokenStoreKey = (typeof TokenStoreKeys)[keyof typeof TokenStoreKeys];

export interface PendingLoginContext {
  verifier: string;
  state: string;
  returnPath: string;
  createdAt: number; // epoch ms
}

export const TokenStore = {
  setTokens(tokens: StoredTokens): void {
    window.sessionStorage.setItem(TokenStoreKeys.tokens, JSON.stringify(tokens));
  },

  getTokens(): StoredTokens | null {
    const raw = window.sessionStorage.getItem(TokenStoreKeys.tokens);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'idToken' in parsed &&
        'accessToken' in parsed &&
        'expiresAt' in parsed &&
        typeof (parsed as Record<string, unknown>)['idToken'] === 'string' &&
        typeof (parsed as Record<string, unknown>)['accessToken'] === 'string' &&
        typeof (parsed as Record<string, unknown>)['expiresAt'] === 'number'
      ) {
        return parsed as StoredTokens;
      }
      return null;
    } catch {
      return null;
    }
  },

  clear(): void {
    sessionStorage.removeItem(TokenStoreKeys.tokens);
    sessionStorage.removeItem(TokenStoreKeys.pendingLogin);
  },

  isExpired(tokens: StoredTokens, skewSeconds = 60): boolean {
    const nowEpochSeconds = Math.floor(Date.now() / 1000);
    return tokens.expiresAt - skewSeconds <= nowEpochSeconds;
  },

  setPendingLogin(ctx: PendingLoginContext): void {
    sessionStorage.setItem(TokenStoreKeys.pendingLogin, JSON.stringify(ctx));
  },

  getPendingLogin(): PendingLoginContext | null {
    const raw = sessionStorage.getItem(TokenStoreKeys.pendingLogin);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as PendingLoginContext;
    } catch {
      return null;
    }
  },

  clearPendingLogin(): void {
    sessionStorage.removeItem(TokenStoreKeys.pendingLogin);
  },
};
